<template>
  <v-otp-input ref="otpInput" class="brand-otp-field" v-bind="mergedAttrs">
    <template v-for="(_, slotName) in $slots" :key="slotName" #[slotName]="slotData">
      <slot :name="slotName" v-bind="slotData || {}" />
    </template>
  </v-otp-input>
</template>

<script setup>
import { computed, useAttrs, ref } from 'vue'
import { mergeAttrs } from '@/utils/vue-attrs'

// Define props
const props = defineProps({
  variant: {
    type: String,
    default: null,
  },
})

// Get attrs
const attrs = useAttrs()

// Template ref
const otpInput = ref(null)

// Define variants
const VARIANTS = {
  basic: {
    'bg-color': 'neutral0',
    'base-color': 'blue400',
    classes: ['brand-otp-field--basic', 'rounded-lg'],
    variant: 'outlined',
  },
}

// Computed common attributes
const commonAttrs = computed(() => {
  const variant = VARIANTS[props.variant]
  const classes = []
  const common = {
    type: 'number',
    length: 6,
  }

  return {
    ...common,
    ...variant,
    class: [
      ...classes,
      ...(variant?.classes ?? []),
    ],
  }
})

// Merge attributes properly
const mergedAttrs = computed(() => {
  return mergeAttrs(commonAttrs.value, attrs)
})

// Methods
const focus = () => {
  otpInput.value?.$el.querySelector('input')?.focus()
}

// Expose methods for parent component access
defineExpose({
  focus,
})

// Set inheritAttrs to false
defineOptions({
  inheritAttrs: false,
})
</script>

<script>
export default {
  inheritAttrs: false,
}
</script>

<style lang="scss" scoped>
.v-otp-input.brand-otp-field {
  &.brand-otp-field--basic:deep(.v-input) {
    &:not(.v-input--disabled) {
      &.v-text-field--outlined:not(.v-field--error):not(:focus-within) .v-field__outline {
        color: rgb(var(--v-theme-neutral50));
      }
    }
  }
}

</style>

<style lang="scss">
// Dark mode specific styles - unscoped to ensure proper cascade
.v-theme--dark {
  .v-otp-input.brand-otp-field.brand-otp-field--basic:deep(.v-input:not(.v-input--disabled)) {
    .v-text-field--outlined:not(.v-field--error):not(:focus-within) .v-field__outline {
      color: rgb(var(--v-theme-neutral50)) !important;
    }
  }
}
</style>
